// hooks/useCourseData.ts
import { useState, useCallback } from 'react';
import { toBackendSemesterCode } from '../utils/semesterUtils';
import { typedApi } from '../services/typedApiClient';
import { getErrorMessage } from '../utils/errorUtils';

// =============================================================================
// MODULE-LEVEL AVAILABILITY CACHE
// =============================================================================
// Whether a course is typically offered in a given term is static for the
// session, but the same (course, semester) pair is queried by many independent
// badge instances (e.g. SuggestionCard renders the badge in both its collapsed
// and expanded views, and the same course can appear across multiple cards).
// Without coalescing this fans out into dozens of duplicate requests on load.
// `_availabilityCache` memoizes completed results; `_availabilityInFlight`
// coalesces concurrent requests so identical lookups share one network call.
// Errors are intentionally NOT cached so a transient failure can be retried.
const _availabilityCache = new Map<string, CourseAvailabilityResult>();
const _availabilityInFlight = new Map<string, Promise<CourseAvailabilityResult>>();

/** Clear the module-level availability cache. Intended for test isolation. */
export const _resetAvailabilityCache = (): void => {
  _availabilityCache.clear();
  _availabilityInFlight.clear();
};

// =============================================================================
// TYPES
// =============================================================================

/**
 * Course data from search results
 */
export interface CourseSearchResult {
  code: string;
  title?: string;
  credits?: number;
  description?: string;
  department?: string;
  gen_ed_requirements?: string[];
  [key: string]: unknown;
}

/**
 * Course statistics
 */
export interface CourseStats {
  totalCourses: number;
  departments: number;
  lastUpdated: string;
  [key: string]: unknown;
}

/**
 * Course availability check result
 */
/** Shape returned by the backend's shared resolve_next_offering rule. */
export interface NextOffering {
  semester_code?: string;
  semester?: string;
  year?: number;
  display?: string;
}

export interface CourseAvailabilityResult {
  course_code: string;
  semester: string;
  is_offered: boolean | null;
  /** Next term the course is offered, when the backend can determine one.
   *  Rendered by <AvailabilityWarning/>. Structured, not a string — render
   *  `.display`; String()-ing the object yields "[object Object]". */
  next_offering?: NextOffering | null;
  note?: string;
  error?: string;
}

/**
 * Course operations interface
 */
export interface CourseOps {
  searchCourses: (queryTerm: string) => Promise<CourseSearchResult[]>;
  setSearchTerm: (term: string) => void;
  getCourseDetails: (courseCode: string) => Promise<unknown>;
  findCourseDetails: (courseCode: string) => CourseSearchResult | null;
  fetchStats: () => Promise<CourseStats>;
  setCourses: (courses: CourseSearchResult[]) => void;
  setError: (error: string | null) => void;
  checkCourseAvailability: (courseCode: string, semester: string) => Promise<CourseAvailabilityResult>;
  getCourseAvailability: (courseCode: string) => Promise<unknown>;
}

/**
 * Return type for the useCourseData hook
 */
export interface UseCourseDataReturn {
  courses: CourseSearchResult[];
  loading: boolean;
  error: string | null;
  searchTerm: string;
  stats: CourseStats | null;
  courseOps: CourseOps;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Custom hook for managing course data and API calls.
 * Migrated to use typedApi for type safety.
 */
export const useCourseData = (): UseCourseDataReturn => {
  const [courses, setCourses] = useState<CourseSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState<CourseStats | null>(null);

  /**
   * Fetch course statistics
   */
  const fetchStats = useCallback(async (): Promise<CourseStats> => {
    try {
      const data = await typedApi.stats.get();
      const statsResponse = data as { stats?: CourseStats } & CourseStats;
      const statsData = statsResponse.stats || statsResponse;
      setStats(statsData as CourseStats);
      return statsData as CourseStats;
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError(getErrorMessage(err));
      // Return default stats to prevent crashes
      const defaultStats: CourseStats = {
        totalCourses: 0,
        departments: 0,
        lastUpdated: new Date().toISOString()
      };
      setStats(defaultStats);
      return defaultStats;
    }
  }, []);

  /**
   * Search for courses
   */
  const searchCourses = useCallback(async (queryTerm: string): Promise<CourseSearchResult[]> => {
    if (!queryTerm || queryTerm.length < 2) {
      setCourses([]);
      return [];  // Return empty array instead of undefined
    }

    setLoading(true);
    setError(null);

    try {
      const data = await typedApi.courses.search({ search: queryTerm, limit: 50 });
      const response = data as unknown as { courses?: CourseSearchResult[] } & CourseSearchResult[];
      const coursesData = response.courses || response;
      setCourses(coursesData as CourseSearchResult[]);
      return coursesData as CourseSearchResult[];  // Return the search results for immediate use
    } catch (err) {
      console.error('Error searching courses:', err);
      setError('Backend not available - course search is disabled');
      setCourses([]);
      return [];  // Return empty array on error
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get course details
   */
  const getCourseDetails = useCallback(async (courseCode: string): Promise<unknown> => {
    try {
      const data = await typedApi.courses.get(courseCode);
      return data;
    } catch (err) {
      console.error('Error fetching course details:', err);
      setError(getErrorMessage(err));
      return null;
    }
  }, []);

  /**
   * Find course details from loaded courses
   */
  const findCourseDetails = useCallback((courseCode: string): CourseSearchResult | null => {
    return courses.find(course => course.code === courseCode) || null;
  }, [courses]);

  /**
   * Check if a course is offered in a specific semester
   */
  const checkCourseAvailability = useCallback(async (
    courseCode: string,
    semester: string
  ): Promise<CourseAvailabilityResult> => {
    // FIXED: Skip availability checks for transfer credits
    // Transfer credits represent completed courses and don't need availability checking
    if (semester === '8' || semester === '-1' || semester === '000008') {
      return {
        course_code: courseCode,
        semester: semester,
        is_offered: true, // Transfer credits are always "available" since they're completed
        note: 'Transfer credit - availability check skipped'
      };
    }

    // Convert frontend semester format to backend format
    const backendSemester = toBackendSemesterCode(semester);
    const cacheKey = `${courseCode}|${backendSemester}`;

    // Serve a previously-resolved result without a network call.
    const cached = _availabilityCache.get(cacheKey);
    if (cached) return cached;

    // Coalesce concurrent requests for the same (course, semester).
    const inFlight = _availabilityInFlight.get(cacheKey);
    if (inFlight) return inFlight;

    const request = (async (): Promise<CourseAvailabilityResult> => {
      try {
        const data = await typedApi.courses.checkSemester(courseCode, backendSemester);
        const response = data as unknown as { check?: CourseAvailabilityResult } & CourseAvailabilityResult;
        const result = response.check || response;
        _availabilityCache.set(cacheKey, result);
        return result;
      } catch (err) {
        console.error('Error checking course availability:', err);
        // Return a default response on error; do NOT cache so it can retry.
        return {
          course_code: courseCode,
          semester: semester,
          is_offered: null, // null means we couldn't determine
          error: getErrorMessage(err)
        };
      } finally {
        _availabilityInFlight.delete(cacheKey);
      }
    })();

    _availabilityInFlight.set(cacheKey, request);
    return request;
  }, []);

  /**
   * Get full availability information for a course
   */
  const getCourseAvailability = useCallback(async (courseCode: string): Promise<unknown> => {
    try {
      const data = await typedApi.courses.getAvailability(courseCode);
      const response = data as { availability?: unknown } & unknown;
      return (response as { availability?: unknown }).availability || response;
    } catch (err) {
      console.error('Error fetching course availability:', err);
      return null;
    }
  }, []);

  // Group all operations under courseOps to match App.js expectations
  const courseOps: CourseOps = {
    searchCourses,
    setSearchTerm,
    getCourseDetails,
    findCourseDetails,
    fetchStats,
    setCourses,
    setError,
    checkCourseAvailability,
    getCourseAvailability,
  };

  return {
    // State
    courses,
    loading,
    error,
    searchTerm,
    stats,

    // Operations grouped
    courseOps,
  };
};
